import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ThemeProvider } from '@mui/material/styles'
import { heapEvent } from '@equipmentshare/heaper'
import { pickFlowTheme } from '../../../../config/muiTheme'
import { useCreatePickOrderMutation, usePickOrderAssigneesQuery } from '../../../../graphql'
import { PICK_ORDER_PREFIX } from '../constants'
import { AssigneeOption, assigneeOptionLabel, toAssigneeOptions } from '../assigneeOptions'
import { useCurrentAssignee } from '../useCurrentAssignee'
import { useShowError, useShowSuccess } from '../../../../hooks/useToast'

/** One fulfillment order the run will cover, as the dialog names it. */
export type PickOrderOrderSummary = {
	id: string,
	orderNumber: number,
}

export type CreatePickOrderDialogProps = {
	open: boolean,
	onClose: () => void,
	fulfillingInventoryId: string,
	/**
	 * The orders the run covers, in the order they were chosen. Which orders may
	 * be chosen together is settled before the dialog opens — by the selection
	 * constraint on the fulfillment orders table — so there is nothing to add or
	 * remove here.
	 */
	orders: PickOrderOrderSummary[],
	/** Where they are all going. Display only; the rule is inventory-api's. */
	destinationDescription?: string | null,
}

// Below this the assignee search is not worth firing.
export const MIN_ASSIGNEE_SEARCH_LENGTH = 2

export default function CreatePickOrderDialog( {
	open,
	onClose,
	fulfillingInventoryId,
	orders,
	destinationDescription,
}: CreatePickOrderDialogProps ) {
	const navigate = useNavigate()
	const showSuccess = useShowSuccess()
	const showError = useShowError()

	const currentAssignee = useCurrentAssignee()
	// `undefined` is "not chosen yet", which falls back to whoever is signed in.
	// `null` is a deliberate clearing of the field, which has to survive — so the
	// two cannot be collapsed, and the default cannot be seeded into state (the
	// token can resolve after the first render, and re-seeding would undo the
	// clearing).
	const [ assigneeChoice, setAssigneeChoice ] = useState<AssigneeOption | null | undefined>( undefined )
	const assignee = assigneeChoice === undefined ? currentAssignee : assigneeChoice
	const [ assigneeSearch, setAssigneeSearch ] = useState( '' )

	const { data: usersData, loading: usersLoading } = usePickOrderAssigneesQuery( {
		variables: { search: assigneeSearch },
		skip: !open || assigneeSearch.trim().length < MIN_ASSIGNEE_SEARCH_LENGTH,
	} )

	const searchedOptions = toAssigneeOptions( usersData?.users.edges.map( ( edge ) => edge.node ) ?? [] )
	// The signed-in person is always offered, ahead of the search results: it is
	// the common answer, it saves typing your own name, and it keeps the default
	// value among the options so the field can be returned to after a change.
	const assigneeOptions = currentAssignee && !searchedOptions.some( ( option ) => option.id === currentAssignee.id )
		? [ currentAssignee, ...searchedOptions ]
		: searchedOptions

	const [ createPickOrder, { loading: isCreating } ] = useCreatePickOrderMutation()

	const close = () => {
		setAssigneeChoice( undefined )
		setAssigneeSearch( '' )
		onClose()
	}

	const handleCreate = async() => {
		try {
			const result = await createPickOrder( {
				variables: {
					input: {
						fulfillingInventoryId,
						fulfillmentOrderIds: orders.map( ( order ) => order.id ),
						assignedToUserId: assignee?.id ?? null,
					},
				},
				refetchQueries: [ 'PickOrders' ],
			} )

			const created = result.data?.createPickOrder
			if ( !created?.success || !created.pickOrder ) {
				heapEvent( 'Inventory - DC Admin - Pick Orders - Create - Submit - Error', {} )
				showError( 'createPickOrder', created?.message ?? 'Could not create the pick order.' )
				return
			}

			heapEvent( 'Inventory - DC Admin - Pick Orders - Create - Submit - Success', {
				pick_order_id: created.pickOrder.id,
				fulfillment_order_count: orders.length,
				assigned: !!assignee,
			} )
			showSuccess( 'createPickOrder', `Pick order ${PICK_ORDER_PREFIX}${created.pickOrder.pickOrderNumber} created.` )
			close()
			navigate( `/dc-admin/pick-orders/${created.pickOrder.id}` )
		} catch ( err ) {
			heapEvent( 'Inventory - DC Admin - Pick Orders - Create - Submit - Error', {} )
			showError( 'createPickOrder', err instanceof Error ? err.message : 'Could not create the pick order.' )
		}
	}

	return (
		<ThemeProvider theme={pickFlowTheme}>
			<Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
				<DialogTitle>Create Pick Order</DialogTitle>
				<DialogContent>
					<Stack spacing={2.5} sx={{ pt: 1 }}>
						<Box>
							<Typography variant="subtitle2" gutterBottom>Picking</Typography>
							<Paper variant="outlined" sx={{ p: 2, borderColor: 'pickFlow.cardBorder' }}>
								<Typography variant="body1" fontWeight={700}>
									{orders.map( ( order ) => `FO-${order.orderNumber}` ).join( ', ' )}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{destinationDescription ?? 'No destination'}
								</Typography>
							</Paper>
						</Box>

						<Autocomplete<AssigneeOption>
							options={assigneeOptions}
							loading={usersLoading}
							value={assignee}
							onChange={( _event, next ) => setAssigneeChoice( next )}
							onInputChange={( _event, next ) => setAssigneeSearch( next )}
							getOptionLabel={assigneeOptionLabel}
							isOptionEqualToValue={( option, selected ) => option.id === selected.id}
							noOptionsText={
								assigneeSearch.trim().length < MIN_ASSIGNEE_SEARCH_LENGTH
									? 'Type a name to search'
									: 'No matching people'
							}
							renderInput={( params ) => (
								<TextField
									{...params}
									label="Assign to (optional)"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{usersLoading ? <CircularProgress size={18} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={close} color="inherit" disabled={isCreating}>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleCreate}
						disabled={isCreating || orders.length === 0}
						startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : undefined}
					>
						Create pick order ({orders.length})
					</Button>
				</DialogActions>
			</Dialog>
		</ThemeProvider>
	)
}
